import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { 
  X, Radio, Eye, Mic, MicOff, Video, VideoOff,
  RotateCcw, Send, ShoppingBag, ChevronUp, ChevronDown,
  User, Heart, Share2, Sparkles, Gift, Star, Gavel, Timer, Trophy, Wifi, WifiOff
} from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import { useAgora } from "@/hooks/useAgora";

// Mock comments for demo
const MOCK_COMMENTS = [
  { id: 1, user: 'fashionista_22', message: 'OMG love this! 😍', type: 'comment' },
  { id: 2, user: 'collector_mike', message: 'Is this authentic?', type: 'comment' },
  { id: 3, user: 'deal_hunter', message: 'joined', type: 'join' },
  { id: 4, user: 'shopper_jane', message: 'Great price!', type: 'comment' },
  { id: 5, user: 'bargain_queen', message: 'liked the show', type: 'like' },
];

const MobileBroadcaster = ({ 
  stream, 
  products = [], 
  onEndStream,
  user 
}) => {
  const navigate = useNavigate();
  const { streamId } = useParams();
  const videoContainerRef = useRef(null);
  const wsRef = useRef(null);
  const [isLive, setIsLive] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [viewerCount, setViewerCount] = useState(12);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState(MOCK_COMMENTS);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [showProducts, setShowProducts] = useState(false);
  const [streamDuration, setStreamDuration] = useState(0);
  const [agoraInitialized, setAgoraInitialized] = useState(false);
  const agoraInitRef = useRef(false); // Ref to prevent double initialization in StrictMode
  
  // Agora hook for video streaming
  const { 
    isConnected: agoraConnected, 
    isPublishing,
    error: agoraError,
    connectionState,
    joinAsHost,
    toggleCamera: agoraToggleCamera,
    toggleMicrophone: agoraToggleMicrophone,
    switchCamera: agoraSwitchCamera,
    leave: agoraLeave
  } = useAgora(streamId);
  
  // Auction state
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [auctionDuration, setAuctionDuration] = useState(30);
  const [isAuctionActive, setIsAuctionActive] = useState(false);
  const [auctionCountdown, setAuctionCountdown] = useState(null);
  const [currentBid, setCurrentBid] = useState(null);
  const [highBidder, setHighBidder] = useState(null);
  const [winner, setWinner] = useState(null);
  
  // Auction Queue state
  const [auctionQueue, setAuctionQueue] = useState([]);
  const [showQueueMode, setShowQueueMode] = useState(false);

  // Mock products if none provided
  const mockProducts = products.length > 0 ? products : [
    { id: 1, title: "Designer Handbag - Limited Edition", price: 99, size: "One Size", brand: "Coach", image: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400" },
    { id: 2, title: "Vintage Sneakers Collection", price: 75, size: "10", brand: "Nike", image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400" },
    { id: 3, title: "Antique Watch - 1960s Era", price: 250, size: "N/A", brand: "Omega", image: "https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=400" },
  ];

  const currentProduct = mockProducts[currentProductIndex];

  // WebSocket connection for real-time updates with reconnection logic
  const wsReconnectAttempts = useRef(0);
  const wsReconnectTimer = useRef(null);
  const wsMaxReconnects = 5;
  const wsIsUnmounting = useRef(false);
  
  const connectWebSocket = useCallback(() => {
    if (!streamId || wsIsUnmounting.current || wsReconnectAttempts.current >= wsMaxReconnects) return;
    
    // Clean up any existing connection
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/api/live/ws/${streamId}?user_id=${user?.user_id || 'host'}&user_name=${encodeURIComponent(user?.name || 'Host')}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Host WebSocket connected');
        wsReconnectAttempts.current = 0; // Reset on successful connection
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch {
          // Silently ignore parse errors
        }
      };
      
      wsRef.current.onclose = () => {
        // Only reconnect if still live and not unmounting
        if (!wsIsUnmounting.current && document.visibilityState === 'visible' && isLive) {
          wsReconnectAttempts.current++;
          if (wsReconnectAttempts.current < wsMaxReconnects) {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s
            const delay = Math.min(1000 * Math.pow(2, wsReconnectAttempts.current - 1), 16000);
            wsReconnectTimer.current = setTimeout(connectWebSocket, delay);
          }
        }
      };
      
      wsRef.current.onerror = () => {
        // Error is handled by onclose, don't log to avoid console spam
      };
    } catch {
      // Connection failed, will be handled by reconnection logic
    }
  }, [streamId, user, isLive]);

  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'chat':
        setComments(prev => [...prev.slice(-8), {
          id: Date.now(),
          user: data.sender_name,
          message: data.content,
          type: 'comment'
        }]);
        break;
        
      case 'viewer_joined':
        setViewerCount(data.viewer_count);
        setComments(prev => [...prev.slice(-8), {
          id: Date.now(),
          user: 'Viewer',
          message: 'joined',
          type: 'join'
        }]);
        break;
        
      case 'viewer_left':
        setViewerCount(data.viewer_count);
        break;
        
      case 'new_bid':
        setCurrentBid(data.amount);
        setHighBidder(data.bidder_name);
        setComments(prev => [...prev.slice(-8), {
          id: Date.now(),
          user: data.bidder_name,
          message: `bid $${data.amount}`,
          type: 'bid'
        }]);
        toast.success(`${data.bidder_name} bid $${data.amount}!`);
        break;
        
      case 'auction_countdown':
        setAuctionCountdown(data.remaining_seconds);
        if (data.current_bid) setCurrentBid(data.current_bid);
        if (data.high_bidder) setHighBidder(data.high_bidder);
        break;
        
      case 'auction_won':
        setIsAuctionActive(false);
        setWinner(data.winner_name);
        // Mark item as completed in queue
        setAuctionQueue(prev => prev.map(item => 
          item.status === 'active' ? { ...item, status: 'completed' } : item
        ));
        toast.success(`🎉 ${data.winner_name} won for $${data.winning_bid}!`, { duration: 5000 });
        setTimeout(() => {
          setWinner(null);
          setCurrentBid(null);
          setHighBidder(null);
          setAuctionCountdown(null);
        }, 5000);
        break;
        
      case 'auction_ended_no_winner':
        setIsAuctionActive(false);
        setAuctionCountdown(null);
        // Mark item as completed (no winner) in queue
        setAuctionQueue(prev => prev.map(item => 
          item.status === 'active' ? { ...item, status: 'completed' } : item
        ));
        toast.info('Auction ended with no bids');
        break;
    }
  };

  // Start auction
  const startAuction = async () => {
    if (!currentProduct || !streamId) return;
    
    try {
      const response = await api.post(`/live/streams/${streamId}/auction/start`, {
        listing_id: currentProduct.id?.toString() || `product_${currentProductIndex}`,
        duration: auctionDuration,
        starting_price: currentProduct.price,
        listing_title: currentProduct.title,  // Send title for mock products
        listing_image: currentProduct.image   // Send image for mock products
      });
      
      setIsAuctionActive(true);
      setAuctionCountdown(auctionDuration);
      setCurrentBid(currentProduct.price);
      setShowAuctionModal(false);
      toast.success(`Auction started! ${auctionDuration} seconds`);
    } catch (err) {
      console.error('Auction start error:', err);
      toast.error(err.response?.data?.detail || 'Failed to start auction');
    }
  };

  // End auction early
  const endAuctionEarly = async () => {
    if (!streamId) return;
    
    try {
      await api.post(`/live/streams/${streamId}/auction/end`);
      toast.success('Auction ended!');
    } catch (err) {
      toast.error('Failed to end auction');
    }
  };

  // Auction Queue Functions
  const addToQueue = (product) => {
    const queueItem = {
      id: product.id,
      listing_id: product.id?.toString() || `product_${Date.now()}`,
      listing_title: product.title,
      listing_image: product.image,
      title: product.title,
      image: product.image,
      duration: auctionDuration,
      starting_price: product.price,
      status: 'pending'
    };
    setAuctionQueue(prev => [...prev, queueItem]);
    toast.success(`Added "${product.title}" to auction queue`);
  };

  const removeFromQueue = (index) => {
    setAuctionQueue(prev => prev.filter((_, i) => i !== index));
    toast.info('Removed from queue');
  };

  const startNextFromQueue = async () => {
    if (isAuctionActive) {
      toast.error('End current auction first');
      return;
    }
    
    const nextItem = auctionQueue.find(item => item.status === 'pending');
    if (!nextItem) {
      toast.info('No more items in queue');
      return;
    }
    
    // Update queue status locally
    setAuctionQueue(prev => prev.map(item => 
      item.listing_id === nextItem.listing_id 
        ? { ...item, status: 'active' } 
        : item
    ));
    
    // Start the auction
    try {
      const response = await api.post(`/live/streams/${streamId}/auction/start`, {
        listing_id: nextItem.listing_id,
        duration: nextItem.duration,
        starting_price: nextItem.starting_price,
        listing_title: nextItem.listing_title || nextItem.title,  // Send title for mock products
        listing_image: nextItem.listing_image || nextItem.image   // Send image for mock products
      });
      
      setIsAuctionActive(true);
      setAuctionCountdown(nextItem.duration);
      setCurrentBid(nextItem.starting_price);
      setShowProducts(false);
      toast.success(`Auction started! ${nextItem.duration} seconds`);
    } catch (err) {
      // Revert status on error
      setAuctionQueue(prev => prev.map(item => 
        item.listing_id === nextItem.listing_id 
          ? { ...item, status: 'pending' } 
          : item
      ));
      console.error('Auction queue start error:', err);
      toast.error(err.response?.data?.detail || 'Failed to start auction');
    }
  };

  // Initialize Agora broadcasting
  const initializeAgora = useCallback(async () => {
    // Use ref for synchronous check to prevent race conditions in StrictMode
    if (agoraInitRef.current || !streamId) return;
    agoraInitRef.current = true; // Set immediately before any async operations
    
    try {
      const success = await joinAsHost(videoContainerRef.current);
      if (success) {
        setAgoraInitialized(true);
        toast.success('Live streaming started!');
      } else {
        toast.error('Failed to start live stream');
        agoraInitRef.current = false; // Reset ref if join failed
      }
    } catch (err) {
      console.error('Agora init error:', err);
      toast.error('Failed to initialize streaming');
      agoraInitRef.current = false; // Reset ref on error
    }
  }, [streamId, joinAsHost]);

  // Show Agora errors
  useEffect(() => {
    if (agoraError) {
      toast.error(`Streaming error: ${agoraError}`);
    }
  }, [agoraError]);

  useEffect(() => {
    // Initialize Agora for live streaming
    initializeAgora();
    
    // Delay WebSocket connection slightly to avoid StrictMode race condition
    const wsConnectTimeout = setTimeout(() => {
      if (!wsIsUnmounting.current) {
        connectWebSocket();
      }
    }, 100);
    
    // Simulate viewer joins
    const viewerInterval = setInterval(() => {
      setViewerCount(v => v + Math.floor(Math.random() * 3));
      
      // Random join notification
      if (Math.random() > 0.6) {
        const names = ['style_lover', 'deal_finder', 'fashion_pro', 'bargain_hunter', 'trendy_shopper'];
        const randomName = names[Math.floor(Math.random() * names.length)];
        setComments(prev => [...prev.slice(-8), {
          id: Date.now(),
          user: randomName,
          message: 'joined',
          type: 'join'
        }]);
      }
    }, 5000);

    // Simulate comments
    const commentInterval = setInterval(() => {
      const messages = [
        'Love this! 😍', 'How much for shipping?', 'Is this still available?', 
        'Beautiful! 🔥', 'What size?', 'Can you show the back?', 'NWT?',
        'I want this!', 'Next item please!', '❤️❤️❤️'
      ];
      const names = ['buyer_22', 'style_fan', 'deal_seeker', 'fashion_pro'];
      
      setComments(prev => [...prev.slice(-8), {
        id: Date.now(),
        user: names[Math.floor(Math.random() * names.length)],
        message: messages[Math.floor(Math.random() * messages.length)],
        type: 'comment'
      }]);
    }, 4000);

    // Simulate likes
    const likeInterval = setInterval(() => {
      if (Math.random() > 0.5) {
        setLikeCount(l => l + 1);
      }
    }, 3000);

    // Stream duration timer
    const durationInterval = setInterval(() => {
      setStreamDuration(d => d + 1);
    }, 1000);

    return () => {
      clearInterval(viewerInterval);
      clearInterval(commentInterval);
      clearInterval(likeInterval);
      clearInterval(durationInterval);
      clearTimeout(wsConnectTimeout);
      agoraInitRef.current = false; // Reset on unmount for StrictMode
      wsIsUnmounting.current = true; // Prevent WebSocket reconnection
      if (wsReconnectTimer.current) {
        clearTimeout(wsReconnectTimer.current);
      }
      agoraLeave();
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {}
        wsRef.current = null;
      }
    };
  }, [connectWebSocket, initializeAgora, agoraLeave]);

  const flipCamera = async () => {
    setIsFrontCamera(!isFrontCamera);
    try {
      await agoraSwitchCamera();
    } catch (err) {
      console.error('Camera flip error:', err);
    }
  };

  const toggleMute = async () => {
    try {
      const newState = await agoraToggleMicrophone();
      setIsMuted(!newState);
    } catch (err) {
      console.error('Mute toggle error:', err);
    }
  };

  const toggleVideo = async () => {
    try {
      const newState = await agoraToggleCamera();
      setIsVideoOff(!newState);
    } catch (err) {
      console.error('Video toggle error:', err);
    }
  };

  const handleEndStream = async () => {
    if (confirm('Are you sure you want to end your live show?')) {
      await agoraLeave();
      onEndStream?.();
      navigate('/my-shows');
      toast.success('Stream ended!');
    }
  };

  const nextProduct = () => {
    setCurrentProductIndex((prev) => 
      prev < mockProducts.length - 1 ? prev + 1 : 0
    );
    toast.success('Featuring next item!');
  };

  const prevProduct = () => {
    setCurrentProductIndex((prev) => 
      prev > 0 ? prev - 1 : mockProducts.length - 1
    );
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col" data-testid="mobile-broadcaster">
      {/* Camera Preview - Full Screen (Agora Container) */}
      <div className="absolute inset-0">
        <div
          ref={videoContainerRef}
          className={`w-full h-full ${isFrontCamera ? 'scale-x-[-1]' : ''}`}
          style={{ background: '#000' }}
        />
        {/* Connection Status Indicator */}
        {!agoraConnected && agoraInitialized && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="bg-black/70 rounded-lg px-4 py-2 flex items-center gap-2">
              <WifiOff className="h-5 w-5 text-yellow-400 animate-pulse" />
              <span className="text-white text-sm">Reconnecting...</span>
            </div>
          </div>
        )}
        {!agoraInitialized && (
          <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-white">Starting live stream...</p>
            </div>
          </div>
        )}
        {isVideoOff && (
          <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
            <div className="text-center">
              <VideoOff className="h-16 w-16 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400">Camera Off</p>
            </div>
          </div>
        )}
        {/* Dark gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/50 pointer-events-none" />
      </div>

      {/* Top Bar */}
      <div className="relative z-10 flex items-center justify-between p-4 safe-area-top">
        {/* Left - End Stream */}
        <button 
          onClick={handleEndStream}
          className="w-10 h-10 bg-red-500/90 rounded-full flex items-center justify-center"
        >
          <X className="h-6 w-6 text-white" />
        </button>

        {/* Center - Live Badge & Timer */}
        <div className="flex items-center gap-2">
          <Badge className={`${agoraConnected && isPublishing ? 'bg-red-500' : 'bg-yellow-500'} text-white px-3 py-1 ${agoraConnected && isPublishing ? 'animate-pulse' : ''}`}>
            {agoraConnected && isPublishing ? (
              <>
                <Radio className="h-3 w-3 mr-1" />
                LIVE
              </>
            ) : (
              <>
                <Wifi className="h-3 w-3 mr-1" />
                CONNECTING
              </>
            )}
          </Badge>
          <span className="text-white text-sm font-mono bg-black/40 px-2 py-1 rounded">
            {formatDuration(streamDuration)}
          </span>
        </div>

        {/* Right - Viewers & Flip Camera */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-white bg-black/40 px-2 py-1 rounded-full">
            <Eye className="h-4 w-4" />
            <span className="text-sm font-medium">{viewerCount}</span>
          </div>
          <button 
            onClick={flipCamera}
            className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center"
          >
            <RotateCcw className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Floating Hearts Animation */}
      {likeCount > 0 && (
        <div className="absolute right-4 bottom-1/3 z-10 pointer-events-none">
          {[...Array(Math.min(likeCount, 5))].map((_, i) => (
            <Heart 
              key={i} 
              className="h-6 w-6 text-red-500 fill-red-500 absolute animate-float-up"
              style={{ 
                animationDelay: `${i * 0.2}s`,
                right: `${Math.random() * 20}px`
              }}
            />
          ))}
        </div>
      )}

      {/* Live Comments - Floating on left */}
      <div className="relative z-10 flex-1 flex flex-col justify-end px-4 pb-4 pointer-events-none">
        <div className="space-y-2 max-h-[200px] overflow-hidden">
          {comments.map((comment) => (
            <div key={comment.id} className="flex items-start gap-2 animate-slide-up pointer-events-auto">
              <Avatar className="w-7 h-7 flex-shrink-0">
                <AvatarFallback className={`text-xs ${
                  comment.type === 'join' ? 'bg-emerald-500' : 
                  comment.type === 'like' ? 'bg-red-500' : 'bg-slate-600'
                } text-white`}>
                  {comment.user[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="bg-black/50 backdrop-blur-sm rounded-2xl px-3 py-1.5">
                <span className="text-white text-sm">
                  <strong>{comment.user}</strong>{' '}
                  {comment.type === 'join' ? (
                    <span className="text-emerald-400">joined 👋</span>
                  ) : comment.type === 'like' ? (
                    <span className="text-red-400">❤️</span>
                  ) : comment.type === 'bid' ? (
                    <span className="text-yellow-400">💰 {comment.message}</span>
                  ) : (
                    <span className="text-white/80">{comment.message}</span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Winner Announcement */}
      {winner && (
        <div className="absolute left-4 bottom-[320px] z-20 animate-bounce-in">
          <div className="flex items-center gap-2 bg-emerald-500/90 rounded-full px-4 py-2">
            <Trophy className="w-5 h-5 text-yellow-300" />
            <span className="text-white text-sm"><strong>{winner}</strong> won!</span>
          </div>
        </div>
      )}

      {/* Auction Countdown Overlay */}
      {isAuctionActive && auctionCountdown !== null && (
        <div className="absolute left-4 bottom-[280px] z-20">
          <div className={`flex items-center gap-2 rounded-full px-4 py-2 ${
            auctionCountdown <= 5 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
          }`}>
            <Timer className="w-5 h-5 text-white" />
            <span className="text-white font-bold text-xl">{auctionCountdown}s</span>
            {highBidder && (
              <span className="text-white/90 text-sm ml-2">
                <strong>{highBidder}</strong> ${currentBid}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bottom Controls */}
      <div className="relative z-10 bg-black/80 backdrop-blur safe-area-bottom">
        {/* Current Product - Swipeable */}
        <div className="p-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            {/* Prev Button */}
            <button 
              onClick={prevProduct}
              disabled={isAuctionActive}
              className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center disabled:opacity-50"
            >
              <ChevronUp className="h-4 w-4 text-white" />
            </button>

            {/* Product Card */}
            <div 
              onClick={() => !isAuctionActive && setShowProducts(true)}
              className="flex-1 flex items-center gap-3 bg-white/10 rounded-xl p-2 cursor-pointer"
            >
              <div className="w-14 h-14 flex-shrink-0 relative">
                <img 
                  src={currentProduct?.image} 
                  alt="" 
                  className="w-full h-full object-cover rounded-lg"
                />
                {isAuctionActive ? (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                    <Gavel className="h-3 w-3 text-white" />
                  </div>
                ) : (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                    <Sparkles className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium line-clamp-1">{currentProduct?.title}</p>
                <p className="text-white/60 text-xs">
                  {isAuctionActive ? 'AUCTION LIVE' : 'Featuring Now'}
                </p>
              </div>
              <div className="text-right">
                {isAuctionActive ? (
                  <>
                    <p className="text-orange-400 font-bold">${currentBid || currentProduct?.price}</p>
                    {highBidder && <p className="text-yellow-400 text-xs">{highBidder}</p>}
                  </>
                ) : (
                  <>
                    <p className="text-emerald-400 font-bold">${currentProduct?.price}</p>
                    <p className="text-white/40 text-xs">{currentProductIndex + 1}/{mockProducts.length}</p>
                  </>
                )}
              </div>
            </div>

            {/* Next/Auction Button */}
            {isAuctionActive ? (
              <button 
                onClick={endAuctionEarly}
                className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            ) : (
              <button 
                onClick={nextProduct}
                className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center"
              >
                <ChevronDown className="h-4 w-4 text-white" />
              </button>
            )}
          </div>
          
          {/* Start Auction Button */}
          {!isAuctionActive && (
            <button
              onClick={() => setShowAuctionModal(true)}
              className="mt-2 w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 font-medium"
            >
              <Gavel className="h-4 w-4" />
              Start Auction
            </button>
          )}
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-around p-4">
          {/* Mute */}
          <button 
            onClick={toggleMute}
            className={`flex flex-col items-center gap-1 ${isMuted ? 'text-red-400' : 'text-white'}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isMuted ? 'bg-red-500/20' : 'bg-white/10'}`}>
              {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </div>
            <span className="text-xs">{isMuted ? 'Unmute' : 'Mute'}</span>
          </button>

          {/* Video Toggle */}
          <button 
            onClick={toggleVideo}
            className={`flex flex-col items-center gap-1 ${isVideoOff ? 'text-red-400' : 'text-white'}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isVideoOff ? 'bg-red-500/20' : 'bg-white/10'}`}>
              {isVideoOff ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
            </div>
            <span className="text-xs">{isVideoOff ? 'Show' : 'Hide'}</span>
          </button>

          {/* Feature Next */}
          <button 
            onClick={nextProduct}
            className="flex flex-col items-center gap-1 text-emerald-400"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Gift className="h-6 w-6" />
            </div>
            <span className="text-xs">Next Item</span>
          </button>

          {/* Products */}
          <button 
            onClick={() => setShowProducts(true)}
            className="flex flex-col items-center gap-1 text-white"
          >
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center relative">
              <ShoppingBag className="h-6 w-6" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full text-xs flex items-center justify-center font-bold">
                {mockProducts.length}
              </span>
            </div>
            <span className="text-xs">Products</span>
          </button>

          {/* End Stream */}
          <button 
            onClick={handleEndStream}
            className="flex flex-col items-center gap-1 text-red-400"
          >
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <X className="h-6 w-6" />
            </div>
            <span className="text-xs">End</span>
          </button>
        </div>
      </div>

      {/* Products Drawer with Auction Queue */}
      {showProducts && (
        <div className="absolute inset-0 z-30 bg-black/60" onClick={() => setShowProducts(false)}>
          <div 
            className="absolute bottom-0 left-0 right-0 bg-slate-900 rounded-t-3xl max-h-[70vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-lg text-white">{mockProducts.length} Items</h3>
                <button onClick={() => setShowProducts(false)}>
                  <X className="h-6 w-6 text-slate-400" />
                </button>
              </div>
              {/* Queue Toggle */}
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowQueueMode(false)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                    !showQueueMode ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  Feature Items
                </button>
                <button 
                  onClick={() => setShowQueueMode(true)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                    showQueueMode ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  Auction Queue ({auctionQueue.length})
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto max-h-[55vh] p-4 space-y-3">
              {showQueueMode ? (
                /* Auction Queue View */
                <>
                  {auctionQueue.length === 0 ? (
                    <div className="text-center py-8">
                      <Gavel className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No items in auction queue</p>
                      <p className="text-slate-500 text-sm">Tap items below to add them</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-slate-400 text-sm">{auctionQueue.length} items queued</p>
                        <button 
                          onClick={startNextFromQueue}
                          disabled={isAuctionActive}
                          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          <Timer className="h-4 w-4" />
                          Start Next
                        </button>
                      </div>
                      {auctionQueue.map((item, idx) => (
                        <div 
                          key={item.listing_id || idx}
                          className={`flex items-center gap-3 p-3 rounded-xl ${
                            item.status === 'active' ? 'bg-orange-500/20 border-2 border-orange-500' :
                            item.status === 'completed' ? 'bg-slate-800/50 opacity-50' :
                            'bg-slate-800 border border-slate-700'
                          }`}
                        >
                          <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-bold">
                            {idx + 1}
                          </div>
                          <img src={item.listing_image || item.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium line-clamp-1">{item.listing_title || item.title}</p>
                            <p className="text-slate-400 text-xs">{item.duration}s auction • ${item.starting_price}</p>
                          </div>
                          {item.status === 'pending' && (
                            <button 
                              onClick={() => removeFromQueue(idx)}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          {item.status === 'active' && (
                            <Badge className="bg-orange-500 text-white animate-pulse">LIVE</Badge>
                          )}
                          {item.status === 'completed' && (
                            <Badge className="bg-emerald-500/50 text-emerald-300">SOLD</Badge>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                  
                  {/* Add more items section */}
                  <div className="border-t border-slate-700 pt-4 mt-4">
                    <p className="text-slate-400 text-sm mb-3">Tap to add to queue:</p>
                    {mockProducts.filter(p => !auctionQueue.find(q => q.id === p.id)).map((product, index) => (
                      <div 
                        key={product.id}
                        onClick={() => addToQueue(product)}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-orange-500 cursor-pointer mb-2"
                      >
                        <img src={product.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                        <div className="flex-1">
                          <p className="text-white text-sm">{product.title}</p>
                          <p className="text-emerald-400 text-sm font-bold">${product.price}</p>
                        </div>
                        <div className="text-orange-400">
                          <ChevronUp className="h-5 w-5" />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                /* Feature Items View */
                mockProducts.map((product, index) => (
                  <div 
                    key={product.id}
                    onClick={() => { 
                      setCurrentProductIndex(index); 
                      setShowProducts(false);
                      toast.success(`Now featuring: ${product.title}`);
                    }}
                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition ${
                      currentProductIndex === index 
                        ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                        : 'bg-slate-800 border border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="relative w-16 h-16 flex-shrink-0">
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      {currentProductIndex === index && (
                        <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/40 rounded-lg">
                          <Star className="h-6 w-6 text-white fill-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm line-clamp-1">{product.title}</p>
                      <p className="text-slate-400 text-xs">{product.brand} • Size: {product.size}</p>
                      {currentProductIndex === index && (
                        <Badge className="mt-1 bg-emerald-500 text-white text-xs">Currently Featuring</Badge>
                      )}
                    </div>
                    <p className="font-bold text-emerald-400">${product.price}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auction Duration Modal */}
      {showAuctionModal && (
        <div className="absolute inset-0 z-40 bg-black/80 flex items-center justify-center" onClick={() => setShowAuctionModal(false)}>
          <div 
            className="bg-slate-900 rounded-2xl p-6 mx-4 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-xl font-bold mb-2 flex items-center gap-2">
              <Gavel className="h-6 w-6 text-orange-400" />
              Start Auction
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Auction for: <strong className="text-white">{currentProduct?.title}</strong>
            </p>
            <p className="text-slate-400 text-sm mb-4">
              Starting price: <strong className="text-emerald-400">${currentProduct?.price}</strong>
            </p>
            
            <p className="text-white font-medium mb-3">Select Countdown Duration:</p>
            
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[10, 30, 60].map((duration) => (
                <button
                  key={duration}
                  onClick={() => setAuctionDuration(duration)}
                  className={`py-4 rounded-xl font-bold text-lg transition ${
                    auctionDuration === duration 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {duration}s
                </button>
              ))}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowAuctionModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={startAuction}
                className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Timer className="h-5 w-5" />
                Start {auctionDuration}s
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float-up {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-100px) scale(1.5); }
        }
        @keyframes bounce-in {
          0% { opacity: 0; transform: scale(0.8); }
          50% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        .animate-float-up { animation: float-up 2s ease-out forwards; }
        .animate-bounce-in { animation: bounce-in 0.4s ease-out; }
        .safe-area-top { padding-top: env(safe-area-inset-top, 12px); }
        .safe-area-bottom { padding-bottom: env(safe-area-inset-bottom, 0); }
      `}</style>
    </div>
  );
};

export default MobileBroadcaster;
